import {reactive} from 'vue';
import {mappedFields} from '@/lib/StandardFields';
import {aggregators} from '../aggregators/competitive';
import {aggregators as leaseAggregators} from '../aggregators/comps/lease.js';
import {omitProperties, onlyOwnProperties} from '@/lib/ReusableFunctions/Object';
import {runAggregators, plainObjectToDF} from './shared';
import {geohashForLocation} from 'geofire-common';
import {MapService} from '@/services/MapService';
import {getStore} from '@/services/store';
import {getRouter} from '@/services/router';
import {Markers} from '@/services/MapService';
import {NEW_BUILDING_ID} from '@/lib/symbols';
import {approveOM, handleDeleteOm, omConfig} from '@/services/OMService/index';
import {bulkConfig} from '@/services/BulkStagingService/config';
import {approveBulkUnit, deleteBulkItem} from '@/services/BulkStagingService/commit';
import {findValidParentPortfolioId} from '@/lib/underwriting/adapters/scenario/parentPortfolio';

// Classes
import {InfoWindowContent} from '../InfoWindowContent';
import {currentUser, addDocument, updateDocument, GeoPoint, getDocument} from '@/services/FirestoreService';
import {getCollection as getFilterCollection} from '@/lib/filters/filters.js';
import {getGroupForComp} from '@/lib/filters/groups.js';
import {DisplayField, displayFieldsToObjects} from '@/lib/classes/InfoWindowContent/DisplayField';
import {wrapError} from '@northpoint-development/labs-common-core';
import {OverlayService} from '@/services/OverlaysService';

/**
 * @typedef {import('@/lib/classes/InfoWindowContent/aggregators/shared.js').Aggregator} Aggregator
 * @typedef {import('@/lib/classes/InfoWindowContent/DisplayField').DisplayField} DisplayField
 * @typedef {import('@/lib/classes/InfoWindowContent/InfoWindowContent').InfoWindowContentData} InfoWindowContentData
 *
 * @typedef Comp
 * @property {string} id
 * @property {string} type
 * @property {string} name
 * @property {DisplayField[]} displayFields
 * @property {InfoWindowContent} parent
 * @property {string} group
 */

const getUnderwritingDashboardToolbarBtn = (kind, id) => {
  return {
    icon: 'UnderwritingDashboard',
    name: 'Underwriting Dashboard',
    callback: async () => {
      let targetKind = kind;
      let targetId = id;

      // Only portfolios and standalone buildings have scenarios. A building linked to a valid OM-generated portfolio
      // redirects to the parent portfolio's dashboard.
      if (kind === 'building') {
        try {
          const parentPortfolioId = await findValidParentPortfolioId(id);
          if (parentPortfolioId) {
            targetKind = 'portfolio';
            targetId = parentPortfolioId;
          }
        } catch (error) {
          console.error(wrapError(error, `Failed to check parent portfolio for building ${id}`));
        }
      }

      const route = getRouter().resolve({
        name: 'UnderwritingDashboard',
        params: {kind: targetKind, id: targetId},
      });
      window.open(route.href, '_blank');
    },
  };
};

const getAuditTrailToolbarBtn = (docId, localCollectionName, compId) => {
  return {
    icon: 'AuditTrail',
    name: 'Audit Trail',
    callback: async () => getStore().commit('setAuditTrailDoc', {docId, localCollectionName, compId}),
  };
};

/**
 * Updates the citation status for a display field.
 * @param {DisplayField} displayField The display field to update
 * @param {string} infoWindowId The ID of the InfoWindowContent
 * @param {boolean} deleted Whether the citation is deleted or restored
 * @param {Object} [comp] Optional comp object for updating comp display fields
 * @return {void}
 */
const updateCitationStatus = (displayFieldId, infoWindowId, deleted, compId = null) => {
  if (!displayFieldId) return;

  const iwContent = getStore()
    .state?.infowindow?.infoWindowContents
    ?.find((content) => content.id === infoWindowId)?.content;

  if (iwContent) {
    if (compId) {
      const comp = iwContent.comps.find((c) => c.id === compId);
      if (!comp) return;
      const displayField = comp.displayFields.find((df) => df.uuid === displayFieldId);
      iwContent.updateCompDisplayField(
        comp,
        new DisplayField({...displayField, citation: {
          ...displayField.citation,
          deleted,
        }}),
        displayField,
      );
    } else {
      const displayField = iwContent.displayFields.find((df) => df.uuid === displayFieldId);
      iwContent.updateDisplayField(
        new DisplayField({...displayField, citation: {
          ...displayField.citation,
          deleted,
        }}),
        displayField,
      );
    }
  }
};

/** Custom handlers for citation delete/restore for display fields */
const citationDeleteHandler = (displayFieldUuid, iwContentId) =>
  updateCitationStatus(displayFieldUuid, iwContentId, true);
const citationRestoreHandler = (displayFieldUuid, iwContentId) =>
  updateCitationStatus(displayFieldUuid, iwContentId, false);
const compCitationDeleteHandler = (displayFieldUuid, iwContentId, compId) =>
  updateCitationStatus(displayFieldUuid, iwContentId, true, compId);
const compCitationRestoreHandler = (displayFieldUuid, iwContentId, compId) =>
  updateCitationStatus(displayFieldUuid, iwContentId, false, compId);

const getAggregators = (type) => {
  if (type === 'industrial') return aggregators;
  if (type === 'portfolio') return aggregators.filter(({name}) => name === 'lastModifiedBy');
  return aggregators.filter(({name}) => name !== 'leasedPercentage' && name !== 'totalAvailable');
};

/**
 * Normalizes audit fields by forcing the timestamp to a Date instance.
 * This guards against cached ISO strings being written back to Firestore.
 * @param {{timestamp?: Date|string|null, name?: string, uid?: string}|null|undefined} field
 * @return {{timestamp: Date|null, name?: string, uid?: string}|null|undefined}
 */
const normalizeAuditField = (field) => {
  if (!field) return field;
  let timestamp = field.timestamp;
  if (timestamp && !(timestamp instanceof Date)) {
    timestamp = new Date(timestamp);
  }
  return {...field, timestamp};
};

export const getAggregatedFields = (building, aggregatorNames) => {
  try {
    let filteredAggregators = aggregators;
    if (aggregatorNames) {
      filteredAggregators = aggregators.filter(({name}) => aggregatorNames.includes(name));
    }
    const [aggregatedFields] = runAggregators(filteredAggregators, building);
    return Object.values(aggregatedFields);
  } catch (error) {
    return {};
  }
};

const compAggregatorsMap = {
  'Lease': leaseAggregators,
  // add other comp types here if we add aggregators
};

/**
 * @param {InfoWindowContent} iwContent
 * @param {object} dto
 * @param {Aggregator[]} aggregators
 */
const reAggregateBuilding = (iwContent, dto, aggregators) => {
  try {
    if (!aggregators) return;

    /** @type {[DisplayField[]]} */
    const [aggregatedFields] = runAggregators(aggregators, dto);
    Object.values(aggregatedFields).forEach((field) => {
      // The field as it exists in the Info Window content
      const currentField = iwContent.displayFields?.find((df) => df?.label === field?.label);

      // The aggregator removed a field
      if (field.value === null && field.valueOverridden) {
        iwContent.displayFields = iwContent.displayFields.filter((df) => df.label !== field.label);

        // The aggregator updated a field, potentially overriding its value
      } else if (currentField && field.value !== null) {
        currentField.value = field.value;
        currentField.valueOverridden = field.valueOverridden;

        // The aggregator added a field
      } else if (!currentField && field.value !== null) {
        iwContent.displayFields.push(field);
      }
    });
  } catch (error) {
    error.message = `Error updating building comps for ${iwContent.title} (${iwContent.id}): ${error.message}`;
    throw error;
  }
};

const reAggregateComps = (comps) => {
  comps.forEach((comp) => {
    const compDto = {
      type: comp.type,
      id: comp.id,
      name: comp.name,
      displayFields: displayFieldsToObjects(comp.displayFields),
    };
    if (comp.underwritingData) {
      compDto.underwritingData = comp.underwritingData;
    }
    reAggregateBuilding(comp, compDto, compAggregatorsMap[comp.type]);
  });
};

/**
 * Extracts display fields from a competitive building
 * @param {Object} building The building
 * @param {'industrial' | 'multiFamily' | 'dataCenter'} type The type of building
 * @return {{displayFields: import('../DisplayField').DisplayField[], metadata: Object}}
 */
function extractCompetitiveBuildingDisplayFields(building, type = 'industrial') {
  try {
    const filteredAggregators = getAggregators(type);
    const [aggregatedFields] = runAggregators(filteredAggregators, building);

    // Add standard fields to an array of display fields
    const standardFields = (() => {
      switch (type) {
        case 'dataCenter':
          return mappedFields['dataCenterBuilding'];
        case 'multiFamily':
          return mappedFields['multiFamilyBuilding'];
        case 'portfolio':
          return mappedFields['competitivePortfolio'];
        default:
          return mappedFields['competitiveBuilding'];
      }
    })();

    const displayFields = getStandardDisplayFields(
      building.displayFields,
      standardFields,
      filteredAggregators,
      aggregatedFields,
    );

    // Add the remaining custom fields
    building.displayFields?.forEach((df) => {
      if (standardFields.some((field) => field.label === df.label)) return;
      displayFields.push(plainObjectToDF(df));
    });

    const omitProps = ['comps', 'displayFields'];
    const shallowBuildingCopy = omitProperties(onlyOwnProperties(building), ...omitProps);

    return {displayFields, metadata: shallowBuildingCopy};
  } catch (error) {
    throw new Error(`Error extracting competitive building display fields: ${error.message}`);
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Creates a new InfoWindowContent instance for a given competitive building from Firestore
 * @param {Object} building - the building data we are using to make the content
 * @return {InfoWindowContent} the content that will be used in the infowindow
 * @throws {Error} if there is an error creating the content
 * ---------------------------------------------------------------------------------------------------------------------
 */
function createCompetitiveBuildingContent(building) {
  try {
    const buildingCopy = {...building}; // Defensive copy
    const {displayFields, metadata} = extractCompetitiveBuildingDisplayFields(buildingCopy);
    /** @type {InfoWindowContentData} */
    const contentData = {
      type: 'competitiveBuilding',
      title: buildingCopy.name,
      id: buildingCopy.id,
      tabIcon: building?.markerData && {name: building.markerData.icon},
      location: buildingCopy.location,
      metadata,
      displayFields: displayFields?.map((field) => {
        return new DisplayField({
          ...field,
          citation: field.citation && {
            ...field.citation,
            customDeleteHandler: () => citationDeleteHandler(field.uuid, buildingCopy.id),
            customRestoreHandler: () => citationRestoreHandler(field.uuid, buildingCopy.id),
          },
        });
      }) || [],
      host: {
        firestoreCollection: buildingCopy.firestoreCollection,
        filterCollection: buildingCopy.filterCollection,
        setHighlight: (state) => Markers.setHighlight(`${contentData.type}s`, contentData.id, state),
        closeActions: () => {
          if (Markers.exists(`${contentData.type}s`, buildingCopy.id)) {
            Markers.setDraggable(`${contentData.type}s`, buildingCopy.id, false);
          }
        },
      },
      linkedDocuments: buildingCopy.linkedDocuments,
    };

    // Create info window content
    const infoWindowContent = new InfoWindowContent(contentData);

    // Create comps after making infoWindowContent so that we can set the parent
    const formattedComps = formatComps(buildingCopy.comps, infoWindowContent);
    infoWindowContent.setComps(formattedComps);

    // Return the final infoWindowContent for competitive buildings
    return infoWindowContent;
  } catch (error) {
    const id = building?.id?.description || building.id;
    error.message = `Error creating competitive building content for ${id}: ${error.message}`;
    throw error;
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Creates a new InfoWindowContent instance for a given competitive Multi-Family building from Firestore
 * @param {Object} building - the building data we are using to make the content
 * @return {InfoWindowContent} the content that will be used in the infowindow
 * @throws {Error} if there is an error creating the content
 * ---------------------------------------------------------------------------------------------------------------------
 */
function createCompetitiveMultiFamilyBuildingContent(building) {
  try {
    const buildingCopy = {...building}; // Defensive copy
    const {displayFields, metadata} = extractCompetitiveBuildingDisplayFields(buildingCopy, 'multiFamily');
    /** @type {InfoWindowContentData} */
    const contentData = {
      type: 'multiFamilyBuilding',
      title: buildingCopy.name,
      id: buildingCopy.id,
      tabIcon: building?.markerData && {name: building.markerData.icon},
      location: buildingCopy.location,
      metadata,
      displayFields,
      host: {
        firestoreCollection: buildingCopy.firestoreCollection,
        filterCollection: buildingCopy.filterCollection,
        setHighlight: (state) => Markers.setHighlight(`${contentData.type}s`, contentData.id, state),
        closeActions: () => {
          if (Markers.exists(`${contentData.type}s`, buildingCopy.id)) {
            Markers.setDraggable(`${contentData.type}s`, buildingCopy.id, false);
          }
        },
      },
    };

    // Create info window content
    const infoWindowContent = new InfoWindowContent(contentData);

    // Return the final infoWindowContent for competitive buildings
    return infoWindowContent;
  } catch (error) {
    const id = building?.id?.description || building.id;
    throw new Error(`Error creating multi-family building content for ${id}: ${error.message}`);
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Creates a new InfoWindowContent instance for a given competitive building from Firestore
 * @param {Object} building - the building data we are using to make the content
 * @return {InfoWindowContent} the content that will be used in the infowindow
 * @throws {Error} if there is an error creating the content
 * ---------------------------------------------------------------------------------------------------------------------
 */
function createCompetitiveDataCenterContent(building) {
  try {
    const buildingCopy = {...building}; // Defensive copy
    const {displayFields, metadata} = extractCompetitiveBuildingDisplayFields(buildingCopy, 'dataCenter');
    /** @type {InfoWindowContentData} */
    const contentData = {
      type: 'dataCenterBuilding',
      title: buildingCopy.name,
      id: buildingCopy.id,
      tabIcon: building?.markerData && {name: building.markerData.icon},
      location: buildingCopy.location,
      metadata,
      displayFields,
      host: {
        firestoreCollection: buildingCopy.firestoreCollection,
        filterCollection: buildingCopy.filterCollection,
        setHighlight: (state) => Markers.setHighlight(`${contentData.type}s`, contentData.id, state),
        closeActions: () => {
          if (Markers.exists(`${contentData.type}s`, buildingCopy.id)) {
            Markers.setDraggable(`${contentData.type}s`, buildingCopy.id, false);
          }
        },
      },
    };

    // Create info window content
    const infoWindowContent = new InfoWindowContent(contentData);

    // Return the final infoWindowContent for competitive buildings
    return infoWindowContent;
  } catch (error) {
    const id = building?.id?.description || building.id;
    throw new Error(`Error creating competitive building content for ${id}: ${error.message}`);
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Creates a new InfoWindowContent instance for a given competitive building from Firestore, with the comps filtered
 * to only show the comp with the given id
 * @param {Object} building - the building data we are using to make the content
 * @param {string} compId - the id of the comp we want to show
 * @return {InfoWindowContent} the content that will be used in the infowindow
 * ---------------------------------------------------------------------------------------------------------------------
 */
function createCompetitiveBuildingCompContent(building, compId) {
  try {
    const buildingCopy = {...building}; // Defensive copy
    const {displayFields, metadata} = extractCompetitiveBuildingDisplayFields(buildingCopy);
    /** @type {InfoWindowContentData} */
    const contentData = {
      type: 'competitiveBuildingComp',
      title: buildingCopy.name || '',
      id: compId,
      tabIcon: building?.markerData && {name: building.markerData.icon},
      location: buildingCopy.location,
      selectedComp: compId,
      metadata,
      displayFields: displayFields?.map((field) => {
        return new DisplayField({
          ...field,
          citation: field.citation && {
            ...field.citation,
            customDeleteHandler: () => citationDeleteHandler(field.uuid, compId),
            customRestoreHandler: () => citationRestoreHandler(field.uuid, compId),
          },
        });
      }) || [],
      comps: [],
      host: {
        firestoreCollection: buildingCopy.firestoreCollection,
        filterCollection: buildingCopy.filterCollection,
        // Derive the target from the content's own type + id: a Hydrated subject may have no
        // markerData, and even when present its collection is only set as a render side effect.
        setHighlight: (state) => Markers.setHighlight(`${contentData.type}s`, contentData.id, state),
      },
    };

    // Create info window content
    const infoWindowContent = new InfoWindowContent(contentData);
    const parentInfoWindowContent = createCompetitiveBuildingContent(building);

    // Create comps after making infoWindowContent so that we can set the parent
    const formattedComps = formatComps(buildingCopy.comps, parentInfoWindowContent, true);
    infoWindowContent.setComps(formattedComps);

    // Return the final infoWindowContent for competitive buildings
    return infoWindowContent;
  } catch (error) {
    throw new Error(`Error creating competitive building comp content for ${building.id}: ${error.message}`);
  }
}

/** Older Chrome also needs `returnValue` set to show the prompt. */
const warnBeforeUnload = (event) => {
  event.preventDefault();
  event.returnValue = '';
};

// Counted across review windows, so the first action to finish can't drop another's prompt.
let reviewActionsInFlight = 0;

/**
 * Shared Approve/Delete edit-mode toolbar for a staged (OM or bulk) review window;
 * each button shows a loader while its async action runs.
 */
function buildReviewToolbar({name, onApprove, onDelete}) {
  // Reactive so the toolbar re-renders when a button is disabled or loading.
  const deleteBtn = reactive({icon: 'Text', name: 'Delete', variant: 'outlined', disabled: false, loading: false});
  const approveBtn = reactive({icon: 'Text', name: 'Approve', disabled: false, loading: false});
  let busy = false;
  const runWhileBusy = (btn, verb, action) => async () => {
    // One at a time: a Delete during an Approve's Salesforce call would leave that Deal unlinked.
    if (busy) return;
    busy = true;
    deleteBtn.disabled = approveBtn.disabled = true;
    btn.loading = true;
    if (reviewActionsInFlight++ === 0) {
      window.addEventListener('beforeunload', warnBeforeUnload);
    }
    try {
      await action();
    } catch (error) {
      console.error(`Couldn't ${verb} ${name}:`, error);
      const reason = String(error?.message ?? error).replace(/\.$/, '');
      getStore().dispatch('addErrorMessage',
        `Couldn't ${verb} ${name}: ${reason}. If the problem persists, please contact solutions-engineering@northpointkc.com`);
    } finally {
      if (--reviewActionsInFlight === 0) {
        window.removeEventListener('beforeunload', warnBeforeUnload);
      }
      busy = false;
      deleteBtn.disabled = approveBtn.disabled = false;
      btn.loading = false;
    }
  };
  deleteBtn.callback = runWhileBusy(deleteBtn, 'delete', onDelete);
  approveBtn.callback = runWhileBusy(approveBtn, 'approve', onApprove);
  return [[deleteBtn, approveBtn]];
}

/** Close action shared by review windows: hide the staging marker when the tab closes. */
function hideMarkerOnClose(localCollection, id) {
  return () => {
    if (Markers.exists(localCollection, id)) {
      Markers.setVisible(localCollection, [id], false);
    }
  };
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Creates a new InfoWindowContent instance for a given competitive building from Firestore
 * @param {import('@/services/OMService/types').OMCompetitiveBuilding} building
 * @return {InfoWindowContent} the content that will be used in the infowindow
 * ---------------------------------------------------------------------------------------------------------------------
 */
function createCompetitiveOmContent(omItem) {
  try {
    const config = omConfig[omItem.type];
    /** @type {Partial<InfoWindowContent>} */
    const contentData = {
      type: config.fullType,
      title: omItem.name,
      id: omItem.id,
      tabIcon: {name: config.markerIcon},
      location: {lat: omItem.location.latitude, lng: omItem.location.longitude},
      displayFields: omItem.displayFields?.map((field) => {
        return new DisplayField({
          ...field,
          citation: field.citation && {
            ...field.citation,
            customDeleteHandler: () => citationDeleteHandler(field.uuid, omItem.id),
            customRestoreHandler: () => citationRestoreHandler(field.uuid, omItem.id),
          },
        });
      }) || [],
      metadata: {
        createdOn: omItem.createdOn,
        editedOn: omItem.editedOn,
        geohash: omItem.geohash,
      },
      comps: omItem.comps?.map((comp) => {
        return {
          type: comp.type,
          id: comp.id,
          name: comp.name,
          underwritingData: comp?.underwritingData ?? {},
          displayFields: comp.displayFields?.map((field) => {
            return new DisplayField({
              ...field,
              citation: field.citation && {
                ...field.citation,
                customDeleteHandler: () => compCitationDeleteHandler(field.uuid, omItem.id, comp.id),
                customRestoreHandler: () => compCitationRestoreHandler(field.uuid, omItem.id, comp.id),
              },
            });
          }) || [],
        };
      }) || [],
      host: {
        firestoreCollection: config.firestoreCollection,
        localCollection: config.localCollection,
        getEditModeToolbar: () => buildReviewToolbar({
          name: omItem.name,
          onApprove: () => approveOM(config, omItem.id),
          onDelete: () => handleDeleteOm(config, omItem.id),
        }),
        closeActions: hideMarkerOnClose(config.localCollection, omItem.id),
      },
    };
    return new InfoWindowContent(contentData);
  } catch (error) {
    const id = omItem?.id?.description || omItem.id;
    throw wrapError(error, `Error creating competitive om content for ${id}`);
  }
}

/**
 * Editable review InfoWindow for a staged bulk record. Mirrors
 * {@link createCompetitiveOmContent} via `bulkConfig` (no citations).
 * @param {import('@/services/BulkStagingService/types').StagedRecord} item
 * @return {InfoWindowContent}
 */
function createBulkReviewContent(item) {
  try {
    const config = bulkConfig[item.kind];
    /** @type {Partial<InfoWindowContent>} */
    const contentData = {
      type: config.fullType,
      title: item.name,
      id: item.id,
      tabIcon: {name: config.markerIcon},
      location: {lat: item.location?.latitude, lng: item.location?.longitude},
      displayFields: item.displayFields?.map((field) => new DisplayField(field)) || [],
      metadata: {
        createdOn: item.uploadedBy,
        editedOn: item.uploadedBy,
        geohash: null,
      },
      comps: item.comps?.map((comp) => ({
        type: comp.type,
        id: comp.id,
        name: comp.name,
        underwritingData: {},
        displayFields: comp.displayFields?.map((field) => new DisplayField(field)) || [],
      })) || [],
      host: {
        firestoreCollection: config.stagingCollection,
        localCollection: config.localCollection,
        getEditModeToolbar: () => buildReviewToolbar({
          name: item.name,
          onApprove: () => approveBulkUnit(item),
          onDelete: () => deleteBulkItem(item),
        }),
        closeActions: hideMarkerOnClose(config.localCollection, item.id),
      },
    };
    return new InfoWindowContent(contentData);
  } catch (error) {
    throw wrapError(error, `Error creating bulk review content for ${item?.id}`);
  }
}

function createCompetitivePortfolioContent(portfolio) {
  try {
    const portfolioCopy = {...portfolio}; // Defensive copy
    const {displayFields, metadata} = extractCompetitiveBuildingDisplayFields(portfolioCopy, 'portfolio');
    /** @type {InfoWindowContentData} */
    const contentData = {
      type: 'competitivePortfolio',
      title: portfolioCopy.name,
      id: portfolioCopy.id,
      tabIcon: portfolioCopy.markerData && {name: portfolioCopy.markerData.icon},
      location: portfolioCopy.location,
      metadata,
      displayFields: displayFields?.map((field) => new DisplayField(field)) || [],
      host: {
        firestoreCollection: portfolioCopy.firestoreCollection,
        filterCollection: portfolioCopy.filterCollection,
        setHighlight: (state) => Markers.setHighlight(`${contentData.type}s`, contentData.id, state),
        closeActions: () => {
          if (Markers.exists(`${contentData.type}s`, portfolioCopy.id)) {
            Markers.setDraggable(`${contentData.type}s`, portfolioCopy.id, false);
          }
        },
      },
      linkedDocuments: portfolioCopy.linkedDocuments,
    };

    // if the id is a symbol, it is new and won't have overlays.
    // if it is a string, it might have overlays
    if (typeof portfolioCopy.id === 'string') {
      contentData.overlays = OverlayService.overlaysByParent(portfolioCopy.id);
    }

    // Create info window content
    const infoWindowContent = new InfoWindowContent(contentData);

    return infoWindowContent;
  } catch (error) {
    throw wrapError(error, `Error creating competitive portfolio content for portfolio ${portfolio.id}`);
  }
}

/**
 * ---------------------------------------------------------------------------------------------------------------------
 * Returns an object with the data needed for creating a marker.
 * @param {Object} comp The comp to create data for.
 * @param {InfoWindowContent} building The parent building.
 * @param {number} index The comp's location within `building.comps`
 * @return {Object} Marker data
 * ---------------------------------------------------------------------------------------------------------------------
 */
function createMarkerDataForComp(comp, building, index) {
  const store = getStore();
  const point = MapService.createCircle(
    building.location['_lat'],
    building.location['_long'],
    building.comps.length,
  );

  const compColor = store.getters['collections/colorFromCompGroup'](getGroupForComp(comp));
  const flatColor = compColor === 'Cyan' ? 'LightBlue' : compColor;
  const icon = comp.generatedByOm
    ? `${compColor}-Balloon`
    : `${flatColor}-Balloon-Flat`;

  return {
    type: 'comp',
    id: comp.id,
    collection: 'competitiveBuildingComps',
    name: comp.name,
    // Assign correct point based on index within 'building.comps'
    latLng: point[index],
    icon,
    events: [{
      event: 'click',
      callback: () => {
        const subject = getFilterCollection(`competitiveBuildingComps`).getItem(comp.id);
        store.dispatch(`infowindow/createCompTab`,
          {subject},
          {root: true},
        );
      },
    }],
  };
}

/**
 * @param {Object} building
 */
function updateBuildingComps(building) {
  const comps = building.comps.map((comp, index) =>
    Object.assign({
      parent: building,
      group: getGroupForComp(comp),
      markerData: createMarkerDataForComp(comp, building, index),
    }, comp),
  );
  // 'addItems' checks if the ids already exist in the collection and replaces as necessary.
  // We don't need to call 'removeItems' because of this.
  const collection = getFilterCollection('competitiveBuildingComps');
  if (collection) {
    collection.addItems(...comps);
  }
}

/**
 * Returns a JSON representation of a competitive building with existing values converted to objects suitable for saving
 * to the database. Values which must be updated on save are not included. (ex: lastEditedDate, geohash)
 * @param {InfoWindowContent} building
 * @return {object}
 */
function competitiveBuildingToJSON(building) {
  if (!building.displayFields) {
    building.displayFields = [];
  }
  const json = {
    id: building.id,
    name: building.title ?? null,
    location: building?.location ? new GeoPoint(building.location.lat(), building.location.lng()) : null,
    createdOn: building?.metadata?.createdOn ?? null,
    editedOn: building?.metadata?.editedOn ?? null,
    generatedByOm: building.metadata?.generatedByOm ?? false,
    // Assume that if a field is not editable, it should not be saved because it is an aggregated field
    displayFields: displayFieldsToObjects(building.displayFields.filter((df) => df.allowEdit)),
    deleted: building?.deleted,
  };

  if (building?.comps) {
    json.comps = building.comps.map((comp) => {
      const compDto = {
        type: comp.type,
        id: comp.id,
        name: comp.name ?? '',
        generatedByOm: comp?.generatedByOm ?? false,
        // Assume that if a field is not editable, it should not be saved because it is an aggregated field
        displayFields: displayFieldsToObjects(comp.displayFields.filter((df) => df.allowEdit)),
      };
      if (comp.underwritingData) {
        compDto.underwritingData = comp.underwritingData;
      }
      return compDto;
    });
  }
  if (building?.linkedDocuments) {
    json.linkedDocuments = building.linkedDocuments;
  }
  if (building?.underwritingData) {
    json.underwritingData = building.underwritingData;
  }

  return json;
}

/**
 * @this {InfoWindowContent}
 * @return {Promise<void>}
 */
async function saveCompetitiveBuilding() {
  if (!this) {
    throw new Error('saveCompetitiveBuilding() must only be called after binding it to an InfoWindowContent instance');
  }
  const markerIcon = this.metadata?.generatedByOm ? 'Darker-Blue-Balloon' : 'Blue-Balloon-Flat';
  return saveBuilding.bind(this)(
    'competitiveBuilding',
    'beyondMaps-properties',
    markerIcon,
    'competitiveBuildings',
    'industrial',
    'createCompetitiveBuildingTab',
    ['totalAvailable', 'leasedPercentage'],
  );
}
/**
 * @this {InfoWindowContent}
 * @return {Promise<void>}
 */
async function saveDataCenterBuilding() {
  if (!this) {
    throw new Error('saveDataCenterBuilding() must only be called after binding it to an InfoWindowContent instance');
  }
  return saveBuilding.bind(this)(
    'dataCenterBuilding',
    'beyondMaps-datacenter',
    'Yellow-Balloon',
    'dataCenterBuildings',
    'dataCenterBuilding',
    'createDataCenterBuildingTab',
  );
}
/**
 * @this {InfoWindowContent}
 * @return {Promise<void>}
 */
async function saveMultiFamilyBuilding() {
  if (!this) {
    throw new Error('saveMultiFamilyBuilding() must only be called after binding it to an InfoWindowContent instance');
  }
  return saveBuilding.bind(this)(
    'multiFamilyBuilding',
    'beyondMaps-multifamily',
    'Purple-Balloon',
    'multiFamilyBuildings',
    'multiFamily',
    'createMultiFamilyBuildingTab',
  );
}

/**
 * Shared body for staged (OM or bulk) saves — staging-only, so nothing touches the live
 * competitive collections until approval. The wrappers below are hoisted `function` declarations
 * so `databasePersistFunctions` can reference them during the competitive/InfoWindowContent
 * circular import (a `const` would be in its TDZ).
 */
async function stagedSave(name, fullType, firestoreCollection, markerIcon, infoWindowAction) {
  if (!this) {
    throw new Error(`${name}() must only be called after binding it to an InfoWindowContent instance`);
  }
  return saveBuilding.bind(this)(fullType, firestoreCollection, markerIcon, null, null, infoWindowAction);
}

async function saveCompetitiveOmBuilding() {
  return stagedSave.call(this, 'saveCompetitiveOmBuilding', 'omCompetitiveBuilding', 'beyondMaps-properties-om', 'Darker-Blue-Balloon', 'createCompetitiveOmTab');
}
async function saveCompetitiveOmPortfolio() {
  return stagedSave.call(this, 'saveCompetitiveOmPortfolio', 'omCompetitivePortfolio', 'beyondMaps-portfolios-om', 'Darker-Blue-Portfolio', 'createCompetitiveOmTab');
}
async function saveCompetitiveBulkBuilding() {
  return stagedSave.call(this, 'saveCompetitiveBulkBuilding', 'bulkCompetitiveBuilding', 'beyondMaps-properties-bulk', 'Blue-Balloon-Flat', 'createBulkReviewTab');
}
async function saveCompetitiveBulkPortfolio() {
  return stagedSave.call(this, 'saveCompetitiveBulkPortfolio', 'bulkCompetitivePortfolio', 'beyondMaps-portfolios-bulk', 'Blue-Portfolio-Flat', 'createBulkReviewTab');
}

async function saveCompetitivePortfolio() {
  if (!this) {
    throw new Error('saveCompetitivePortfolio() must only be called after binding it to an InfoWindowContent instance');
  }
  const markerIcon = this.metadata?.generatedByOm ? 'Darker-Blue-Portfolio' : 'Blue-Portfolio-Flat';
  return saveBuilding.bind(this)(
    'competitivePortfolio',
    'beyondMaps-portfolios',
    markerIcon,
    'competitivePortfolios',
    'portfolio',
    'createCompetitivePortfolioTab',
  );
}

/**
 * @param {
 * 'competitiveBuildings'|
 * 'competitivePortfolios'|
 * 'multiFamilyBuildings'|
 * 'dataCenterBuildings'
 * } filterCollection
 * @param {
 * 'createCompetitiveBuildingTab'|
 * 'createCompetitivePortfolioTab'|
 * 'createMultiFamilyBuildingTab'|
 * 'createDataCenterBuildingTab'
 * } infoWindowAction
 * @param {
 * 'beyondMaps-properties'|
 * 'beyondMaps-portfolios'|
 * 'beyondMaps-multifamily'|
 * 'beyondMaps-datacenter'
 * } firestoreCollection
 * @param {string} id
 * @return {Array<{event:string,callback:Function}>}
 */
const createMarkerEventHandlers = (filterCollection, infoWindowAction, firestoreCollection, id) => {
  const store = getStore();
  /** @type {MarkerEvent} */
  const clickEvent = {
    event: 'click',
    callback: async () => {
      const dbDoc = await getDocument(firestoreCollection, id, true);
      const filterDoc = getFilterCollection(filterCollection).getItem(id);
      // preserve fields compute on hydration like 'markerData'.
      const subject = {...filterDoc, ...dbDoc};
      store.dispatch(`infowindow/${infoWindowAction}`,
        {subject: {...subject, filterCollection, firestoreCollection}},
        {root: true},
      );
    },
  };
  /** @type {MarkerEvent} */
  const dragEndEvent = {
    event: 'dragend',
    callback: async (latLng) => {
      const latitude = latLng.lat;
      const longitude = latLng.lng;

      // Save New Position
      const newLocation = {
        latitude,
        longitude,
      };
      const newGeohash = geohashForLocation([
        latitude,
        longitude,
      ]);
      const dbDoc = await getDocument(firestoreCollection, id);
      const filterDoc = getFilterCollection(filterCollection).getItem(id);
      // preserve fields compute on hydration like 'markerData'.
      const updatedBuilding = {...filterDoc, ...dbDoc};
      // update marker data
      updatedBuilding.markerData.latLng = latLng;

      const writableBuilding = {
        ...updatedBuilding,
        displayFields: displayFieldsToObjects(updatedBuilding.displayFields.filter((df) => df.allowEdit)),
        location: newLocation,
        geohash: newGeohash,
        editedOn: {
          timestamp: new Date(),
          name: currentUser().displayName,
          uid: currentUser().uid,
        },
        createdOn: {
          timestamp: new Date(updatedBuilding.createdOn.timestamp),
          name: updatedBuilding.createdOn.name,
          uid: updatedBuilding.createdOn.uid,
        },
      };

      delete writableBuilding.markerData;
      // If 'parent' exists, it is a circular reference, and it should be removed prior to sending to the
      // converter to avoid max callstack errors
      writableBuilding?.comps?.forEach((comp) => {
        delete comp.markerData;
        delete comp.parent;
      });

      try {
        // Update Building Location in Firebase
        await updateDocument(firestoreCollection, id, writableBuilding);
        // Update Marker Info
        getFilterCollection(filterCollection).addItems({
          ...updatedBuilding,
          location: newLocation,
          geohash: newGeohash,
          editedOn: {
            timestamp: new Date(),
            name: currentUser().displayName,
            uid: currentUser().uid,
          },
        });
      } catch (error) {
        throw new Error(`Error saving update for building ${writableBuilding.title} (${writableBuilding.id}): ${error.message} ${error.stack}`);
      }
    },
  };
  return [clickEvent, dragEndEvent];
};

/**
 * @this {InfoWindowContent}
 * @param {'competitiveBuilding'|'multiFamilyBuilding'|'dataCenterBuilding'} type
 * @param {'beyondMaps-properties'|'beyondMaps-multifamily'|'beyondMaps-datacenter'} firestoreCollection
 * @param {'Darker-Blue-Balloon'|'Blue-Balloon-Flat'|'Purple-Balloon'|'Yellow-Balloon'} markerIcon
 * @param {'competitiveBuildings'|'multiFamilyBuildings'|'dataCenterBuildings'} filterCollection
 * @param {'industrial'|'multiFamily'|'dataCenterBuilding'} aggregators
 * @param {
 * 'createCompetitiveBuildingTab'|
 * 'createMultiFamilyBuildingTab'|
 * 'createDataCenterBuildingTab'} infoWindowAction
 * @param {Array<string>} rawAggregators
 * @return {Promise<void>}
 */
async function saveBuilding(
  type,
  firestoreCollection,
  markerIcon,
  filterCollection,
  aggregators,
  infoWindowAction,
  rawAggregators = [],
) {
  if (!this) {
    throw new Error(`saveBuilding() for ${type} must only be called after binding it to an InfoWindowContent instance`);
  }
  const dto = competitiveBuildingToJSON(this);

  // Update `this` with newly calculated values for editedOn, and geohash
  this.metadata.editedOn = {
    timestamp: new Date(),
    name: currentUser().displayName,
    uid: currentUser().uid,
  };
  this.metadata.geohash = this.location ? geohashForLocation([this.location.lat(), this.location.lng()]) : null;
  // apply the new values to the dto
  dto.editedOn = this.metadata.editedOn;
  dto.geohash = this.metadata.geohash;
  // The ID should not be saved as part of the object, so remove it
  delete dto.id;

  if (this.id === NEW_BUILDING_ID) {
    // populate CreatedBy and CreatedDate fields for new buildings. Since this is a new building, the values are the
    // same as the last edited values
    this.metadata.createdOn = {
      timestamp: new Date(),
      name: currentUser().displayName,
      uid: currentUser().uid,
    };
    // apply the new values to the dto
    dto.createdOn = this.metadata.createdOn;
    dto.deleted = null;
    this.id = await addDocument(firestoreCollection, dto);
  } else {
    // Make sure that createdOn is saved as a date object
    this.metadata.createdOn = normalizeAuditField(this.metadata.createdOn);
    dto.createdOn = this.metadata.createdOn;
    try {
      await updateDocument(firestoreCollection, this.id, dto);
    } catch (error) {
      throw new Error(`Error saving update for building ${this.title} (${this.id}): ${error.message} ${error.stack}`);
    }
  }
  if (Array.isArray(rawAggregators) && rawAggregators.length) {
    const fields = getAggregatedFields(dto, rawAggregators);
    dto.displayFields.push(...Object.values(fields).filter(Boolean));
  }
  if (filterCollection) {
    const item = getFilterCollection(filterCollection).getItem(this.id);
    const filterObject = {
      ...dto,
      id: this.id,
      linkedDocuments: item?.linkedDocuments || [],
      underwritingData: item?.underwritingData || {},
      markerData: {
        type: type,
        collection: filterCollection,
        id: this.id,
        name: dto.name,
        latLng: dto.location ? {lat: dto.location.latitude, lng: dto.location.longitude} : null,
        icon: markerIcon,
        events: createMarkerEventHandlers(filterCollection, infoWindowAction, firestoreCollection, this.id),
      },
    };
    // Update the local filter collection with the new comp data
    // 'addItems' checks if the ids already exist in the collection and replaces as necessary.
    // We don't need to call 'removeItems' because of this.
    getFilterCollection(filterCollection).addItems(filterObject);
  }
  if (aggregators) {
    reAggregateBuilding(this, dto, getAggregators(aggregators));
  }
  if (this.comps) {
    reAggregateComps(this.comps);
  }

  return this.id;
}

/**
 * This function is called when updating info window content while in comp view mode
 * @this {InfoWindowContent}
 * @return {Promise<void>}
 */
async function saveCompetitiveBuildingComp() {
  if (!this) {
    throw new Error('saveCompetitiveBuildingComp() must only be called after binding it to an InfoWindowContent instance');
  }

  const parentContent = this.comps?.find((comp) => comp.id === this.id)?.parent;
  const parentDto = competitiveBuildingToJSON(parentContent);

  if (!parentDto) {
    throw new Error(`Competitive building comp does not have parent on ${this.id}`);
  }
  reAggregateBuilding(this, parentDto, getAggregators('industrial'));
  if (this.comps) {
    reAggregateComps(this.comps);
  }

  // Multiple comps from the same parent can be open in the same info window content
  // We need get the latest comps data for each open tab so that changes between tabs are not lost when
  // updating the parent document.
  parentContent.comps.forEach((comp, index) => {
    const infoWindowContent = getStore().state?.infowindow?.infoWindowContents?.find(
      (content) => content.id === comp.id)?.content;
    if (infoWindowContent) {
      parentContent.comps[index] = infoWindowContent?.comps?.find((c) => c.id === comp.id);
      parentDto.comps[index] = competitiveBuildingToJSON(infoWindowContent)?.comps?.find((c) => c.id === comp.id);
    }
  });

  parentContent.metadata.editedOn = {
    timestamp: new Date(),
    name: currentUser().displayName,
    uid: currentUser().uid,
  };
  parentDto.createdOn = normalizeAuditField(parentDto.createdOn);
  parentContent.metadata.geohash = geohashForLocation([parentContent.location.lat(), parentContent.location.lng()]);
  // apply the new values to the dto
  parentDto.editedOn = parentContent.metadata.editedOn;
  parentDto.geohash = parentContent.metadata.geohash;
  await updateDocument('beyondMaps-properties', parentDto.id, parentDto);

  // Recalculate the aggregated display fields for the parent
  const fields = getAggregatedFields(parentDto, ['totalAvailable', 'leasedPercentage']);
  parentDto.displayFields.push(...Object.values(fields).filter(Boolean));

  // Add markerData after saving to Firestore since 'events' contains functions
  parentDto.markerData = {
    type: 'competitiveBuilding',
    collection: 'competitiveBuildings',
    id: parentDto.id,
    name: parentDto.name,
    latLng: {lat: parentDto?.location['_lat'], lng: parentDto?.location['_long']},
    icon: parentDto.generatedByOm ? 'Darker-Blue-Balloon' : 'Blue-Balloon-Flat',
    events: createMarkerEventHandlers('competitiveBuildings', 'createCompetitiveBuildingTab', 'beyondMaps-properties', parentDto.id),
  };

  getFilterCollection('competitiveBuildings').addItems(parentDto);
  updateBuildingComps(parentDto);
}

/**
 * Duplicates a competitive building comp and saves it to Firestore
 * @param {InfoWindowContent} infoWindowContent
 * @param {string} collectionName
 * @param {createCompetitiveBuildingContent |
 *  createCompetitiveMultiFamilyBuildingContent |
 *  createCompetitiveDataCenterContent} createContentAction
 * @return {Promise<InfoWindowContent>}
 */
async function duplicateComp(infoWindowContent, collectionName, createContentAction) {
  const screenPixelLocation = MapService.latLngToScreenPixel(infoWindowContent.location);
  const newLocation = MapService.screenPixelToLatLng(screenPixelLocation.x - 20, screenPixelLocation.y);

  // Create a new building with the same data as the current building
  const dto = competitiveBuildingToJSON(infoWindowContent);
  const comps = dto.comps;
  dto.comps = [];
  dto.id = NEW_BUILDING_ID;
  dto.name = `COPY - ${dto.name ? dto.name : 'Building'}`;
  const createdOn = {
    timestamp: new Date(),
    name: currentUser().displayName,
    uid: currentUser().uid,
  };
  dto.editedOn = createdOn;
  dto.createdOn = createdOn;
  dto.location = new GeoPoint(newLocation.lat(), newLocation.lng());
  dto.geohash = geohashForLocation([newLocation.lat(), newLocation.lng()]);

  const markerData = {
    collection: collectionName,
    id: NEW_BUILDING_ID,
    icon: infoWindowContent.icon.name,
  };

  // Save to firestore to get an ID for the new building
  const newMarkerIWContent = createContentAction({...dto, markerData});
  await newMarkerIWContent.updateSourceData();

  // Remove the ids from the comps so that new comps are created
  if (comps) {
    comps.forEach((comp) => comp.id = null);
    const formattedComps = formatComps(comps, newMarkerIWContent);
    formattedComps.forEach((formattedComp) => {
      newMarkerIWContent.addComp(formattedComp);
    });

    const newBuilding = competitiveBuildingToJSON(newMarkerIWContent);
    updateBuildingComps(newBuilding);
  }

  // Update the host now that the new building has been saved
  if (newMarkerIWContent.host.setHighlight) {
    newMarkerIWContent.host.setHighlight =
      (state) => Markers.setHighlight(markerData.collection, newMarkerIWContent.id, state);
  }

  return newMarkerIWContent;
}

/**
 * Returns the display fields for the given comp
 * @param {Object} comp - the comp object
 * @return {DisplayField[]}
 */
function getCompDisplayFields(comp) {
  const shallowCompCopy = {...comp};

  let filteredAggregators;
  let aggregatedFields;
  if (comp.type === 'Lease') {
    filteredAggregators = leaseAggregators;
    [aggregatedFields] = runAggregators(leaseAggregators, comp);
  }

  const displayFields = getStandardDisplayFields(
    shallowCompCopy.displayFields,
    mappedFields[`competitiveBuilding${comp.type}`],
    filteredAggregators,
    aggregatedFields,
  );

  // Add the remaining custom fields
  shallowCompCopy.displayFields?.forEach((df) => {
    if (mappedFields[`competitiveBuilding${comp.type}`].some((field) => field.label === df.label)) return;
    displayFields.push(plainObjectToDF(df));
  });

  return displayFields;
}

/**
 * Formats the comps for the frontend
 * @param {Array<Object>} comps - the comps to format
 * @param {InfoWindowContent} parentContent - the parent content
 * @param {boolean} compMode - whether the info window content is in comp view mode
 * @return {Comp[]}
 */
const formatComps = (comps, parentContent, compMode = false) => {
  /** @type {Comp[]} */
  const formattedComps = [];
  comps?.forEach((comp) => {
    const displayFields = getCompDisplayFields(comp);

    // Every comp must have an ID. This is not generated by firestore because these are not top level documents.
    // If the comp has no ID, it may be a comp which is new and never saved to the database, so a new ID must be
    // generated.
    if (!comp.id) {
      comp.id = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    }

    const formattedComp = {
      ...comp,
      name: comp.name ?? '',
      parent: parentContent,
      displayFields: displayFields?.map((field) => {
        return new DisplayField({
          ...field,
          citation: field.citation && {
            ...field.citation,
            customDeleteHandler: () => compCitationDeleteHandler(
              field.uuid,
              compMode ? comp.id : parentContent.id,
              comp.id,
            ),
            customRestoreHandler: () => compCitationRestoreHandler(
              field.uuid,
              compMode ? comp.id : parentContent.id,
              comp.id,
            ),
          },
        });
      }) || [],
    };

    if (comp.underwritingData) {
      formattedComp.underwritingData = comp.underwritingData;
    }

    formattedComps.push(formattedComp);
  });
  return formattedComps;
};

/**
 * Returns the standard display fields for a competitive building or comp
 * @param {Array<DisplayField>} buildingDisplayFields - the display fields of the building or comp
 * @param {Array<object>} standardFields - the standard fields for the building or comp type
 * @param {Array<Aggregator>} filteredAggregators - the aggregators that were filtered out
 * @param {Object} aggregatedFields - the aggregated fields for the building or comp
 */
const getStandardDisplayFields = (buildingDisplayFields, standardFields, filteredAggregators, aggregatedFields) => {
  const displayFields = [];
  standardFields.forEach((field) => {
    // If the field has already been aggregated, use its value if it is valid or if the value is not overridden
    if (aggregatedFields && aggregatedFields[field.id] &&
      (aggregatedFields[field.id].value !== null || !aggregatedFields[field.id].valueOverridden)
    ) {
      // Remove the display field from building.displayFields if it exists
      displayFields.push(aggregatedFields[field.id]);
      return;
    }
    // If a field was supposed to be aggregated but did not appear in the aggregatedFields object,
    // That means that there is nothing to add so we can skip it
    if (filteredAggregators && filteredAggregators?.map((aggregator) => aggregator.name).includes(field.id)) return;

    // Push new display fields
    let standardDF = buildingDisplayFields?.find((df) => df.label === field.label);
    if (standardDF) {
      standardDF = plainObjectToDF({
        ...standardDF,
        pickerOptions: field.pickerOptions ?? [],
        unit: standardDF.unit === 'link' ? 'link' : (field.formatUnit ?? field.unit),
      });
    } else {
      standardDF = plainObjectToDF({...field, allowEdit: true});
    }
    displayFields.push(standardDF);
  });
  return displayFields;
};

const formatCompsForExport = (rawContent, id) => {
  const building = {
    ...rawContent.parent, // createCompetitiveBuildingCompContent requires a building as input, not a comp
    location: rawContent.markerData.latLng, // override the parent's location with the specific comp's location
  };
  const infoWindowContent = createCompetitiveBuildingCompContent(building, id);
  const comp = infoWindowContent.comps.find((comp) => comp.id === id);
  infoWindowContent.setComps([]);
  const displayFields = [...comp.displayFields];
  displayFields.unshift(new DisplayField({
    label: 'Comp Type',
    value: comp.type,
    unit: 'string',
    allowDelete: true,
    allowEdit: true,
  }));
  displayFields.push(new DisplayField({
    label: 'Building Name',
    value: infoWindowContent.title,
    unit: 'string',
    allowDelete: true,
    allowEdit: true,
  }));
  displayFields.push(...infoWindowContent.displayFields);
  infoWindowContent.displayFields = displayFields;
  infoWindowContent.title = comp.name ?? '';
  return infoWindowContent;
};

/**
 * Determines if any display field, or any field nested inside one, has a value.
 * @param {Array<Object>} [displayFields]
 * @return {boolean}
 */
const hasAnyDisplayFieldValue = (displayFields) => (displayFields ?? []).some((df) => {
  if (df.value) return true;
  if (Array.isArray(df.displayFields)) {
    return df.displayFields.some((nestedDf) => nestedDf.value);
  }
  return false;
});

/**
 * Determines if any editable display field has a value. Only editable values count: derived ones
 * (e.g. Last Modified By) exist on every building. A non-editable group is not a dead end — an
 * editable field can sit nested inside it.
 * @param {Array<Object>} [displayFields]
 * @return {boolean}
 */
const hasAnyEditableFieldValue = (displayFields) => (displayFields ?? []).some((df) => {
  if (!df.allowEdit) return hasAnyEditableFieldValue(df.displayFields);
  return Boolean(df.value) || hasAnyDisplayFieldValue(df.displayFields);
});

/**
 * Determines if a building is considered "empty" and should be discarded if the user tries to
 * save it. An empty building has no name, no display fields with values, no comps, and no linked
 * documents.
 * @param {string} filterCollection The name of the filter collection holding the building.
 * @param {{id: (string | symbol), title?: string, displayFields?: Array<Object>, comps?: Array<Object>}} liveContent
 *    The in-progress info-window content for the building; its `id` locates the collection copy.
 * @return {boolean} true if the building is empty, false otherwise.
 */
const isEmptyBuilding = (filterCollection, liveContent) => {
  // The collection copy lags the throttled save, so the live form state gets the first say —
  // otherwise naming a building and immediately finishing editing reads as empty and cancels it.
  const hasLiveData = (typeof liveContent.title === 'string' && liveContent.title.trim() !== '') ||
    hasAnyEditableFieldValue(liveContent.displayFields) ||
    (liveContent.comps?.length > 0);
  if (hasLiveData) return false;
  const building = getFilterCollection(filterCollection)?.getItem(liveContent.id);
  if (!building) return true;
  const hasNoName = !building.name || building.name.trim() === '';
  const hasNoDisplayFields = !hasAnyDisplayFieldValue(building.displayFields);
  const hasNoComps = !building.comps || building.comps.length === 0;
  const hasNoLinkedDocuments = !building.linkedDocuments || building.linkedDocuments.length === 0;
  return hasNoName && hasNoDisplayFields && hasNoComps && hasNoLinkedDocuments;
};

/**
 * Determines if a building was generated by an OM and has underwriting data.
 * Takes the hydrated document (e.g. InfoWindowContent metadata) — the filter collection only
 * holds slim markers without underwritingData when backend filtering is enabled.
 */
const hasUnderwritingData = (doc) => {
  return Boolean(doc?.generatedByOm && doc?.underwritingData && Object.keys(doc.underwritingData).length > 0);
};


export {
  getAuditTrailToolbarBtn,
  getUnderwritingDashboardToolbarBtn,

  createCompetitiveBuildingContent,
  createCompetitiveDataCenterContent,
  createCompetitiveMultiFamilyBuildingContent,
  createCompetitiveBuildingCompContent,
  createCompetitiveOmContent,
  createBulkReviewContent,
  extractCompetitiveBuildingDisplayFields,
  createMarkerEventHandlers,
  saveCompetitiveBuilding,
  saveCompetitiveOmBuilding,
  saveCompetitiveOmPortfolio,
  saveCompetitiveBulkBuilding,
  saveCompetitiveBulkPortfolio,
  saveDataCenterBuilding,
  saveMultiFamilyBuilding,
  saveCompetitiveBuildingComp,
  competitiveBuildingToJSON,
  updateBuildingComps,
  duplicateComp,
  reAggregateComps,
  getCompDisplayFields,
  formatComps,
  formatCompsForExport,
  isEmptyBuilding,
  hasUnderwritingData,
  NEW_BUILDING_ID,

  createCompetitivePortfolioContent,
  saveCompetitivePortfolio,
};
